import React, { useMemo, useState } from "react";
import { message, Tooltip } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import CgIcon from "../common/CgIcon";
import { printPharmacyBill } from "../../../services/pharmacyBilling.services";
import styles from "../../PatientsIP/ReceptionistIp/BillingIp/BillingIp.module.css";
import billStyles from "./PharmacyBillMedicines.module.css";
import { formatMoney } from "../common/money";

const money = (v) => formatMoney(v ?? 0);

/*
 * The three ways to read the same bill, and the columns each one needs.
 *
 * Summary is what a customer is handed - what they bought and what it cost.
 * Detailed is what the pharmacy answers questions from: batch, rate and the tax
 * on every line. Returns is what came back, which used to be a seventh column on
 * Detailed that was a dash on almost every row.
 */
const VIEWS = {
  summary: "minmax(0, 2.4fr) minmax(0, 0.7fr) minmax(0, 1fr)",
  detailed:
    "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 0.6fr) minmax(0, 0.8fr) minmax(0, 0.8fr) minmax(0, 0.9fr)",
  returns: "minmax(0, 2.4fr) minmax(0, 0.7fr) minmax(0, 1fr)",
};

/* A batch inside two months of its expiry is worth pointing at. */
const EXPIRY_WARN_DAYS = 60;

const medicineName = (item) =>
  item.pharmacy_medicine_name || item.medicine_name || "—";

const PharmacyBillMedicines = ({ bill }) => {
  const items = useMemo(() => bill?.items || [], [bill?.items]);
  const [view, setView] = useState("detailed");

  /* Only the lines something actually came back on. */
  const returnedItems = useMemo(
    () => items.filter((item) => Number(item.returned_quantity) > 0),
    [items]
  );

  const download = async (type, label) => {
    if (!bill?.id) return;
    try {
      await printPharmacyBill(bill.id, type);
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          `Unable to download the ${label}`
      );
    }
  };

  const columns = VIEWS[view];
  const end = styles.billingTableColEnd;

  const segment = (key, label) => (
    <button
      type="button"
      role="tab"
      aria-selected={view === key}
      className={`${styles.segmentBtn} ${view === key ? styles.segmentBtnActive : ""}`}
      onClick={() => setView(key)}
    >
      {label}
    </button>
  );

  const header =
    view === "summary" ? (
      <>
        <span className={styles.servicesColLabel}>Medicine</span>
        <span className={`${styles.servicesColLabel} ${end}`}>Qty</span>
        <span className={`${styles.servicesColLabel} ${end}`}>Amount</span>
      </>
    ) : view === "returns" ? (
      <>
        <span className={styles.servicesColLabel}>Medicine</span>
        <span className={`${styles.servicesColLabel} ${end}`}>Qty</span>
        <span className={`${styles.servicesColLabel} ${end}`}>Credit</span>
      </>
    ) : (
      <>
        <span className={styles.servicesColLabel}>Medicine</span>
        <span className={styles.servicesColLabel}>Batch</span>
        <span className={`${styles.servicesColLabel} ${end}`}>Qty</span>
        <span className={`${styles.servicesColLabel} ${end}`}>Rate</span>
        <span className={`${styles.servicesColLabel} ${end}`}>GST</span>
        <span className={`${styles.servicesColLabel} ${end}`}>Amount</span>
      </>
    );

  const row = (item) => {
    if (view === "summary") {
      return (
        <>
          <span className={styles.billingTableCellText}>{medicineName(item)}</span>
          <span className={`${styles.billingTableCellText} ${end}`}>{item.quantity}</span>
          <span className={`${styles.billingTableCellAmount} ${end}`}>{money(item.amount)}</span>
        </>
      );
    }

    if (view === "returns") {
      return (
        <>
          <span className={styles.billingTableCellText}>{medicineName(item)}</span>
          <span className={`${styles.billingTableCellText} ${end}`}>
            {item.returned_quantity}
          </span>
          <span className={`${styles.billingTableCellAmount} ${end}`}>
            {money(item.returned_amount)}
          </span>
        </>
      );
    }

    const expiry = item.batch_expiry ? dayjs(item.batch_expiry) : null;
    const nearExpiry =
      expiry?.isValid() && expiry.diff(dayjs(), "day") < EXPIRY_WARN_DAYS;

    return (
      <>
        <span className={styles.billingTableCellText}>{medicineName(item)}</span>
        <span className={styles.servicesCellMuted}>
          <span className={billStyles.batchNo}>{item.batch_number || "—"}</span>
          {expiry?.isValid() ? (
            <span
              className={`${billStyles.expiry} ${nearExpiry ? billStyles.expiryNear : ""}`}
            >
              Exp {expiry.format("DD MMM YYYY")}
            </span>
          ) : null}
        </span>
        <span className={`${styles.billingTableCellText} ${end}`}>{item.quantity}</span>
        <span className={`${styles.billingTableCellText} ${end}`}>{money(item.rate)}</span>
        <span className={`${styles.servicesCellMuted} ${end}`}>
          {item.gst_percentage != null ? (
            <span className={billStyles.gstChip}>{item.gst_percentage}%</span>
          ) : (
            "—"
          )}
          {item.gst_amount != null ? (
            <span className={billStyles.gstAmt}>{money(item.gst_amount)}</span>
          ) : null}
        </span>
        <span className={`${styles.billingTableCellAmount} ${end}`}>{money(item.amount)}</span>
      </>
    );
  };

  const rows = view === "returns" ? returnedItems : items;
  const emptyText =
    view === "returns"
      ? "Nothing has come back off this bill."
      : "No medicines on this bill";

  return (
    <section className={`${styles.servicesCard} ${styles.servicesCardDetailed}`}>
      <div className={styles.servicesCardHeader}>
        <div className={styles.servicesCardHeaderLeft}>
          <span className={billStyles.cardIcon}>
            <CgIcon name="pill" size={18} stroke={2} />
          </span>
          {/* One line, as the mockup heads it - the count is in the table and
              "GST inclusive" is in the totals under it. */}
          <h3 className={styles.sectionCardTitle}>Medicines and Consumables</h3>
        </div>
        <div className={`${styles.servicesHeaderTools} ${billStyles.headerTools}`}>
          <div className={`${styles.segmented} ${billStyles.seg3}`} role="tablist">
            {segment("summary", "Summary")}
            {segment("detailed", "Detailed")}
            {/* The count is the point of the tab: it says whether looking  is
                worth it before you look. */}
            {segment(
              "returns",
              returnedItems.length ? `Returns · ${returnedItems.length}` : "Returns"
            )}
          </div>
          {/*
            * Printing belongs to the two tabs that show the bill.
            *
            * Returns shows what has come back off it, which the bill document
            * is not - the button fell through to the detailed branch and
            * printed the bill instead of what was on screen, which is a
            * printout nobody asked for.
            */}
          {view !== "returns" && (
            <Tooltip title={view === "summary" ? "Print Summary" : "Print Detailed Bill"}>
              <button
                type="button"
                className={styles.downloadBtn}
                onClick={() =>
                  download(
                    view === "summary" ? "summary" : "detailed",
                    view === "summary" ? "summary" : "bill"
                  )
                }
                aria-label={view === "summary" ? "Print Summary" : "Print Detailed Bill"}
              >
                <PrinterOutlined />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {!rows.length ? (
        <p className={styles.servicesEmpty}>{emptyText}</p>
      ) : (
        <div className={styles.servicesBillingTableScroll}>
          <div
            className={styles.servicesTableHeader}
            style={{ gridTemplateColumns: columns }}
          >
            {header}
          </div>
          <div className={styles.servicesTableBody}>
            {rows.map((item) => (
              <div
                key={item.id}
                className={styles.servicesTableRow}
                style={{ gridTemplateColumns: columns }}
              >
                {row(item)}
              </div>
            ))}
          </div>

          {/* What came back is its own view now, so the totals under it would be
              the wrong ones - the bill's figures belong to the bill's lines. */}
          {view !== "returns" && (
            <div
              className={styles.servicesSubtotalRow}
              style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <TotalRow label="Taxable value" value={money(bill?.subtotal)} />
                {Number(bill?.discount) > 0 && (
                  <TotalRow label="Discount" value={`− ${money(bill.discount)}`} />
                )}
                <TotalRow label="GST" value={money(bill?.gst_amount)} />
                {Number(bill?.returned_amount) > 0 && (
                  <TotalRow label="Returned" value={`− ${money(bill.returned_amount)}`} />
                )}
                <div className={billStyles.netRow}>
                  <span className={billStyles.netLabel}>Net payable</span>
                  <span className={billStyles.netValue}>
                    {money(
                      Number(bill?.returned_amount) > 0
                        ? bill?.net_grand_total
                        : bill?.grand_total
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const TotalRow = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 24 }}>
    <span className={styles.servicesSubtotalLabel}>{label}</span>
    <span className={styles.billingTableCellText}>{value}</span>
  </div>
);

export default PharmacyBillMedicines;
